import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Link2, Send, UserRound, Store, Building2, CheckCircle2, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).replace(',', '') : '';

function BankBadge({ status }) {
  if (status?.payouts_enabled) return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">Bank ready</Badge>;
  if (status?.connected) return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">Setup started</Badge>;
  return <Badge variant="outline" className="text-[10px]">No bank yet</Badge>;
}

export function EventPayments({ eventId, walletBalanceCents, onPaid }) {
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payTarget, setPayTarget] = useState(null); // {payee_type, ref_id, name, email, suggested_cents}
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [otherName, setOtherName] = useState('');
  const [otherEmail, setOtherEmail] = useState('');
  const [buyers, setBuyers] = useState([]);
  const [holderEmail, setHolderEmail] = useState('');
  const [holderAmount, setHolderAmount] = useState('');
  const [holderNote, setHolderNote] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/events/${eventId}/payee-status`, auth()),
        axios.get(`${API}/events/${eventId}/payments`, auth()),
      ]);
      setData(s.data); setPayments(p.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load payments'); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/events/${eventId}/buyers`, auth()).then(r => setBuyers(r.data.buyers)).catch(() => {});
  }, [eventId]);

  const sendToHolder = async () => {
    const cents = Math.round(parseFloat(holderAmount || '0') * 100);
    if (!cents || cents < 100) return toast.error('Minimum is $1.00');
    if (cents > (walletBalanceCents || 0)) return toast.error('Not enough in your Eventism wallet');
    setBusy(true);
    try {
      await axios.post(`${API}/organizations/me/wallet/send`, { email: holderEmail, amount_cents: cents, note: holderNote }, auth());
      toast.success(`${usd(cents)} wallet credit sent to ${holderEmail}`);
      setHolderEmail(''); setHolderAmount(''); setHolderNote('');
      onPaid && onPaid();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setBusy(false); }
  };

  if (!data || !payments) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;

  const sendLink = async (target) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/payees/setup-link`, { ...target, send_email: true }, auth());
      if (r.data.emailed) toast.success(`Bank-setup link emailed to ${target.name}`);
      else { await navigator.clipboard.writeText(r.data.url).catch(() => {}); toast.success('Setup link copied — send it to them'); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create setup link'); }
    finally { setBusy(false); }
  };

  const copyLink = async (target) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/payees/setup-link`, target, auth());
      await navigator.clipboard.writeText(r.data.url).catch(() => {});
      toast.success('Setup link copied to clipboard');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create setup link'); }
    finally { setBusy(false); }
  };

  const doPay = async () => {
    const cents = Math.round(parseFloat(amount || '0') * 100);
    if (!cents || cents < 100) return toast.error('Minimum payment is $1.00');
    if (cents > (walletBalanceCents || 0)) return toast.error('Not enough in your Eventism wallet');
    setBusy(true);
    try {
      await axios.post(`${API}/events/${eventId}/pay`, { ...payTarget, amount_cents: cents, note }, auth());
      toast.success(`${usd(cents)} sent to ${payTarget.name} — lands in their bank in 1-2 business days`);
      setPayTarget(null); setAmount(''); setNote('');
      load(); onPaid && onPaid();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
    finally { setBusy(false); }
  };

  const resendReceipt = async (p) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/payments/${p.payment_id}/resend-receipt`, {}, auth());
      toast.success(`Receipt re-sent to ${r.data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not resend receipt'); }
    finally { setBusy(false); }
  };

  const status = data.payee_status || {};
  const paidByRef = payments.paid_by_ref || {};

  const Row = ({ icon: Icon, name, sub, email, target, agreed, refId }) => {
    const st = status[(email || '').toLowerCase()];
    const paid = paidByRef[refId] || 0;
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-border/60 first:border-t-0" data-testid={`pay-row-${refId}`}>
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{name} <BankBadge status={st} /></p>
          <p className="text-[11px] text-muted-foreground truncate">
            {sub}{agreed > 0 && ` · agreed ${usd(agreed)}`}{paid > 0 && ` · paid ${usd(paid)}`}{!email && ' · no email on file'}
          </p>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy || !email}
            onClick={() => sendLink(target)} data-testid={`send-link-${refId}`}>
            <Link2 className="h-3 w-3 mr-1" /> {st?.payouts_enabled ? 'Resend link' : 'Send bank link'}
          </Button>
          <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={busy || !st?.payouts_enabled}
            onClick={() => { setPayTarget({ ...target, name, email }); setAmount(agreed > paid && agreed - paid >= 100 ? ((agreed - paid) / 100).toFixed(2) : ''); }}
            data-testid={`pay-btn-${refId}`}>
            <Send className="h-3 w-3 mr-1" /> Pay
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="event-payments">
      <div>
        <p className="text-sm font-semibold mb-1">Pay your people</p>
        <p className="text-[11px] text-muted-foreground mb-2">Each person connects a bank once (free Stripe setup), then you can pay them from your wallet ({usd(walletBalanceCents)}). Payments arrive in 1–2 business days, no fees.</p>
        <div className="rounded-lg border border-border">
          {(data.staff || []).map(s => (
            <Row key={s.staff_id} icon={UserRound} name={s.name} sub={s.role || 'Staff'} email={s.email}
              agreed={s.pay_cents} refId={s.staff_id}
              target={{ payee_type: 'staff', ref_id: s.staff_id, name: s.name, email: s.email }} />
          ))}
          {(data.vendors || []).map(v => (
            <Row key={v.ev_id} icon={Store} name={v.business_name} sub="Vendor" email={v.business_email}
              agreed={v.vendor_fee_cents} refId={v.ev_id}
              target={{ payee_type: 'vendor', ref_id: v.ev_id, name: v.business_name, email: v.business_email }} />
          ))}
          {(data.staff || []).length === 0 && (data.vendors || []).length === 0 && (
            <p className="px-3 py-5 text-center text-xs text-muted-foreground">No staff or vendors yet — add them under Production, or pay anyone below.</p>
          )}
        </div>
      </div>

      <Card className="border-border/60"><CardContent className="py-3 space-y-2">
        <p className="text-xs font-semibold flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-indigo-400" /> Pay your venue or anyone else</p>
        <div className="flex flex-wrap gap-2">
          <Input className="h-8 text-xs w-44" placeholder="Name (e.g. The Grand Ballroom)" value={otherName} onChange={e => setOtherName(e.target.value)} data-testid="other-payee-name" />
          <Input className="h-8 text-xs w-52" placeholder="Their email" value={otherEmail} onChange={e => setOtherEmail(e.target.value)} data-testid="other-payee-email" />
          <Button size="sm" variant="outline" className="h-8 text-[11px]" disabled={busy || !otherName || !otherEmail}
            onClick={() => sendLink({ payee_type: 'venue', name: otherName, email: otherEmail })} data-testid="other-send-link">
            <Link2 className="h-3 w-3 mr-1" /> Email bank link
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[11px]" disabled={busy || !otherName || !otherEmail}
            onClick={() => copyLink({ payee_type: 'venue', name: otherName, email: otherEmail })} data-testid="other-copy-link">
            Copy link
          </Button>
          <Button size="sm" className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || !otherName || !otherEmail}
            onClick={() => setPayTarget({ payee_type: 'venue', name: otherName, email: otherEmail })} data-testid="other-pay-btn">
            <Send className="h-3 w-3 mr-1" /> Pay
          </Button>
        </div>
      </CardContent></Card>

      <Card className="border-border/60"><CardContent className="py-3 space-y-2">
        <p className="text-xs font-semibold flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-emerald-400" /> Send wallet credit to a ticket holder</p>
        <p className="text-[10px] text-muted-foreground">Goes straight into their Eventism wallet (good for goodwill refunds, prizes, comps) — they get an email and can spend it or withdraw it to their bank.</p>
        <div className="flex flex-wrap gap-2">
          <Input className="h-8 text-xs w-56" list={`buyers-${eventId}`} placeholder="Buyer email" value={holderEmail} onChange={e => setHolderEmail(e.target.value)} data-testid="holder-email-input" />
          <datalist id={`buyers-${eventId}`}>
            {buyers.map(b => <option key={b.email} value={b.email}>{b.name}</option>)}
          </datalist>
          <Input className="h-8 text-xs w-28" type="number" min="1" step="0.01" placeholder="Amount $" value={holderAmount} onChange={e => setHolderAmount(e.target.value)} data-testid="holder-amount-input" />
          <Input className="h-8 text-xs flex-1 min-w-[120px]" placeholder="Note (they'll see it)" value={holderNote} onChange={e => setHolderNote(e.target.value)} data-testid="holder-note-input" />
          <Button size="sm" className="h-8 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy || !holderEmail || !holderAmount}
            onClick={sendToHolder} data-testid="holder-send-btn">
            <Send className="h-3 w-3 mr-1" /> Send credit
          </Button>
        </div>
      </CardContent></Card>

      <div>
        <p className="text-sm font-semibold mb-2">Payments sent {payments.total_paid_cents > 0 && <span className="text-xs font-normal text-muted-foreground">— {usd(payments.total_paid_cents)} total</span>}</p>
        <div className="rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead><tr className="bg-muted/40">
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">To</th>
              <th className="text-left px-3 py-2">Note</th><th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {payments.payments.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-5 text-center text-muted-foreground text-xs">No payments yet.</td></tr>
              ) : payments.payments.map(p => (
                <tr key={p.payment_id} className="border-t border-border/60">
                  <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                  <td className="px-3 py-1.5">{p.payee_name} <span className="text-[10px] text-muted-foreground">({p.payee_type})</span></td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{p.note}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-rose-400">{usd(p.amount_cents)} out</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => resendReceipt(p)} disabled={busy}
                      className="text-[11px] text-indigo-400 hover:underline disabled:opacity-50"
                      data-testid={`resend-receipt-${p.payment_id}`}>Resend receipt</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Pay {payTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /> Comes out of your Eventism wallet ({usd(walletBalanceCents)}) and lands in their bank in 1–2 business days.</p>
            <div>
              <label className="text-xs font-medium">Amount ($)</label>
              <Input type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="150.00" data-testid="pay-amount-input" />
            </div>
            <div>
              <label className="text-xs font-medium">Note (they'll see it)</label>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Door staff — Halloween night" data-testid="pay-note-input" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={doPay} data-testid="pay-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Send ${amount ? usd(Math.round(parseFloat(amount) * 100)) : 'payment'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
